import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CONTRACT_STATUS_MAP, tafqit, type ContractStatus } from '@goyone/shared';
import { api } from '../../lib/api';
import { closePrintView } from '../../lib/printclose';
import { date, money } from '../../lib/format';
import { useTenantProfile } from '../../lib/settings';
import { Logo } from '../../components/Logo';
import { PrintSheet, useDocumentPaper } from '../../components/DocumentPaper';
import '../../styles/print.css';

interface Milestone {
  label?: string;
  title?: string;
  amount?: number | string;
  dueAt?: string | null;
  percent?: number;
}

interface ContractData {
  id: string;
  number: string;
  title: string;
  status: ContractStatus;
  startAt: string;
  endAt: string | null;
  value: string;
  currency: string;
  terms: string | null;
  notes: string | null;
  milestones: Milestone[] | null;
  signedAt: string | null;
  signedByName: string | null;
  signatureUrl: string | null;
  clauses: Array<{ id: string; title: string; body: string }> | null;
  account: {
    id: string;
    name: string;
    legalName: string | null;
    taxNumber: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
  } | null;
}

/**
 * العقد المطبوع.
 *
 * مكوّن مستقل عن عرض السعر والفاتورة رغم تشابه الترويسة.
 *
 * العقد ليس مستند بنود: لا كميات ولا أسعار وحدة، بل مدة وقيمة إجمالية
 * وجدول دفعات وشروط طويلة، وينتهي بتوقيعين لا بإجمالي مستحق. حشره في
 * قالب الفاتورة كان سيترك نصف أعمدته فارغة ويخفي ما يميّزه.
 */
export function PrintContract() {
  const { id = '' } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const autoPrint = params.get('print') === '1';

  const contract = useQuery({
    queryKey: ['print-contract', id],
    queryFn: () => api<ContractData>(`/sales/contracts/${id}`),
  });

  const profile = useTenantProfile();
  /* ورقةُ الشركة (ليتر هيد) إن رُفعت: تحلّ محلّ ترويسة النظام وتذييله. */
  const { paper, ready: paperReady } = useDocumentPaper('letterhead');
  const ready = contract.data && profile.data && paperReady;

  useEffect(() => {
    if (!autoPrint || !ready) return;
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, [autoPrint, ready]);

  if (contract.isError || profile.isError) {
    return (
      <div className="print-page">
        <p>تعذّر تحميل العقد.</p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="print-page">
        <p>جارٍ التحضير…</p>
      </div>
    );
  }

  const { branding, company, tenant } = profile.data!;
  const c = contract.data!;
  const currency = profile.data!.settings.currencyLabel || 'ج.م';
  const milestones = Array.isArray(c.milestones) ? c.milestones : [];
  const clauses = Array.isArray(c.clauses) ? c.clauses : [];

  const partyOne = company.legalName || tenant.name;
  const partyTwo = c.account?.legalName || c.account?.name || '';

  return (
    <div
      className="print-root"
      style={{
        ['--doc-primary' as string]: branding.primary,
        ['--doc-ink' as string]: branding.ink,
      }}
    >
      <div className="print-toolbar no-print">
        <button className="btn btn-primary" onClick={() => window.print()}>
          طباعة أو حفظ PDF
        </button>
        <button className="btn" onClick={closePrintView}>
          إغلاق
        </button>
        <span className="small mute">اختر «حفظ كـ PDF» في نافذة الطباعة للحصول على ملف.</span>
      </div>

      <PrintSheet paper={paper}>
        <header className="doc-header">
          <div className="doc-brand">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="" className="doc-logo" />
            ) : (
              <Logo size={52} />
            )}
            <div>
              <div className="doc-company">{partyOne}</div>
              {branding.documentHeader && (
                <div className="doc-tagline">{branding.documentHeader}</div>
              )}
              <div className="doc-company-meta">
                {company.address && <div>{company.address}</div>}
                <div className="doc-inline">
                  {company.phone && <span className="num">{company.phone}</span>}
                  {company.email && <span className="num">{company.email}</span>}
                </div>
                {branding.showTaxNumber && company.taxNumber && (
                  <div>
                    الرقم الضريبي: <span className="num">{company.taxNumber}</span>
                    {company.commercialRegister && (
                      <>
                        {' — '}س.ت: <span className="num">{company.commercialRegister}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="doc-title-block">
            <div className="doc-kind">عقد</div>
            <div className="doc-number num">{c.number}</div>
            <div className="doc-status">{CONTRACT_STATUS_MAP[c.status]?.label}</div>
          </div>
        </header>

        <h1 className="contract-title">{c.title}</h1>

        {/* ---- الأطراف ---- */}
        <section className="contract-parties">
          <div className="contract-party">
            <div className="doc-label">الطرف الأول</div>
            <div className="doc-party-name">{partyOne}</div>
            {company.address && <div>{company.address}</div>}
            <div className="doc-inline">
              {company.phone && <span className="num">{company.phone}</span>}
              {company.email && <span className="num">{company.email}</span>}
            </div>
            {company.taxNumber && (
              <div>
                الرقم الضريبي: <span className="num">{company.taxNumber}</span>
              </div>
            )}
          </div>

          <div className="contract-party">
            <div className="doc-label">الطرف الثاني</div>
            <div className="doc-party-name">{partyTwo}</div>
            {c.account?.address && <div>{c.account.address}</div>}
            <div className="doc-inline">
              {c.account?.phone && <span className="num">{c.account.phone}</span>}
              {c.account?.email && <span className="num">{c.account.email}</span>}
            </div>
            {c.account?.taxNumber && (
              <div>
                الرقم الضريبي: <span className="num">{c.account.taxNumber}</span>
              </div>
            )}
          </div>
        </section>

        {/* ---- البيانات الأساسية ---- */}
        <section className="contract-facts">
          <div className="contract-fact">
            <span className="doc-label">تاريخ البدء</span>
            <span>{date(c.startAt)}</span>
          </div>
          <div className="contract-fact">
            <span className="doc-label">تاريخ الانتهاء</span>
            <span>{c.endAt ? date(c.endAt) : 'غير محدَّد'}</span>
          </div>
          <div className="contract-fact">
            <span className="doc-label">قيمة التعاقد</span>
            <span className="num strong">{money(c.value, currency)}</span>
          </div>
          {c.signedAt && (
            <div className="contract-fact">
              <span className="doc-label">تاريخ التوقيع</span>
              <span>{date(c.signedAt)}</span>
            </div>
          )}
        </section>

        <div className="doc-words">
          <div className="doc-label">قيمة التعاقد بالحروف</div>
          {/*
            التفقيط على العقد أهم منه على الفاتورة: العقد يُوقَّع ويُحتجّ به،
            وتغيير رقم فيه سهل بينما تغيير جملة مكتوبة ليس كذلك.
          */}
          <div className="doc-words-text">{tafqit(Number(c.value), c.currency)}</div>
        </div>

        {/* ---- جدول الدفعات ---- */}
        {milestones.length > 0 && (
          <>
            <h2 className="contract-section">جدول الدفعات</h2>
            <table className="doc-table">
              <thead>
                <tr>
                  <th style={{ width: 34 }}>م</th>
                  <th>البيان</th>
                  <th style={{ width: 110 }}>الاستحقاق</th>
                  <th style={{ width: 120 }}>القيمة</th>
                </tr>
              </thead>
              <tbody>
                {milestones.map((m, index) => (
                  <tr key={index}>
                    <td className="num center">{index + 1}</td>
                    <td>{m.label ?? m.title ?? `الدفعة ${index + 1}`}</td>
                    <td className="center">{m.dueAt ? date(m.dueAt) : '—'}</td>
                    <td className="num end strong">
                      {m.amount !== undefined
                        ? money(m.amount, currency)
                        : m.percent !== undefined
                          ? money((Number(c.value) * m.percent) / 100, currency)
                          : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* ---- البنود ---- */}
        {clauses.length > 0 && (
          <>
            <h2 className="contract-section">بنود العقد</h2>
            <ol className="contract-clauses">
              {clauses.map((cl) => (
                <li key={cl.id}>
                  {cl.title && <strong>{cl.title}: </strong>}
                  <span>{cl.body}</span>
                </li>
              ))}
            </ol>
          </>
        )}

        {/* ---- الشروط ---- */}
        {c.terms && (
          <>
            <h2 className="contract-section">الشروط والأحكام</h2>
            <div className="doc-text contract-terms">{c.terms}</div>
          </>
        )}

        {c.notes && (
          <>
            <h2 className="contract-section">ملاحظات</h2>
            <div className="doc-text">{c.notes}</div>
          </>
        )}

        {/* ---- التوقيعات ---- */}
        <section className="contract-signatures">
          <div className="contract-sign">
            <div className="doc-label">الطرف الأول</div>
            <div className="contract-sign-name">{partyOne}</div>
            {/*
              توقيع العقد يسبق ختم الهوية.
              الأول خاصٌّ بهذا المستند ومؤرَّخ، والثاني ختمٌ عامّ على كل ورقة.
            */}
            {c.signatureUrl ? (
              <img src={c.signatureUrl} alt="" className="doc-stamp" />
            ) : branding.stampUrl ? (
              <img src={branding.stampUrl} alt="" className="doc-stamp" />
            ) : (
              <div className="doc-sign-line" />
            )}
            <div className="tiny">
              {c.signedByName ? `${c.signedByName} — ` : ''}الاسم والتوقيع
            </div>
          </div>

          <div className="contract-sign">
            <div className="doc-label">الطرف الثاني</div>
            <div className="contract-sign-name">{partyTwo}</div>
            <div className="doc-sign-line" />
            <div className="tiny">الاسم والتوقيع</div>
          </div>
        </section>

        {branding.documentFooter && (
          <footer className="doc-footer">
            <div className="doc-footer-text">{branding.documentFooter}</div>
          </footer>
        )}
      </PrintSheet>
    </div>
  );
}
